import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { AgentDefinitionView } from '@inference.engine/api-client/web';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
// Deep imports on purpose: the slice's index also carries the canvas, which only its page loads.
import { listDefinitions } from '@/features/agent-builder/api/agent-definitions.api';
import { AGENT_BUILDER_QUERY_KEYS } from '@/features/agent-builder/api/query-keys';
import type { AgentDefinitionDocument } from '@/features/agent-builder/model/types';
import type { ToolRowData } from '@/features/admin/ui/ToolsTable';

/**
 * The agents built on the canvas, as rows of the organization's tools table. Drafts are included:
 * the table is where an admin finds an agent to keep building, and `GET /agents` lists only what
 * people can already use. Archived ones are included too, marked, for the filter that finds them
 * again to restore.
 */
export function useBuiltAgentRows(
  enabled: boolean,
  workspaces: readonly WorkspaceResponseDto[],
): { rows: ToolRowData[]; isLoading: boolean; isError: boolean; refetch: () => Promise<unknown> } {
  const { t } = useTranslation('agentBuilder');
  const definitions = useQuery({
    queryKey: AGENT_BUILDER_QUERY_KEYS.list(),
    queryFn: listDefinitions,
    enabled,
  });

  const rows = useMemo(
    () =>
      (definitions.data ?? []).map((view): ToolRowData => {
        const metadata = (view.draft as unknown as Partial<AgentDefinitionDocument>).metadata;
        const excluded = view.scope === 'org' ? (view.excludedWorkspaceIds ?? []) : [];
        const listed = view.scope === 'workspace' ? view.workspaceIds : excluded;
        const inScope = workspaces.filter((ws) => listed.includes(ws.id));
        const status = statusOf(view, t);
        return {
          id: view.id,
          name: metadata?.name ?? t('newAgentName'),
          description: metadata?.description ?? '',
          ...(metadata?.tag ? { tag: metadata.tag } : {}),
          source: 'user',
          workspaceAccess:
            view.scope === 'workspace' ? 'only' : excluded.length > 0 ? 'except' : 'all',
          workspaces: inScope,
          fullyConfiguredWorkspaces: [],
          builtAgent: {
            ...(status ? { status } : {}),
            ...(view.scope === 'user' ? { accessLabel: t('scope.option.user.label') } : {}),
            ...(view.status === 'archived' ? { archived: true } : {}),
          },
        };
      }),
    [definitions.data, workspaces, t],
  );

  return {
    rows,
    isLoading: enabled && definitions.isPending,
    isError: definitions.isError,
    refetch: definitions.refetch,
  };
}

/** Only a state an admin has to act on earns a badge; a cleanly published agent needs none. */
function statusOf(
  view: AgentDefinitionView,
  t: ReturnType<typeof useTranslation<'agentBuilder'>>['t'],
): { label: string; tone: 'neutral' | 'warning' } | undefined {
  if (view.status === 'published') {
    return view.hasUnpublishedChanges
      ? { label: t('list.unpublishedChanges'), tone: 'warning' }
      : undefined;
  }
  return { label: t(`status.${view.status}`), tone: 'neutral' };
}
